import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Lock, ExternalLink } from "lucide-react";
import { canShowPlans, isNativeApp, billingUIEnabled } from "@/lib/platform";
import { openPaywall } from "@/lib/iap";

export default function PaywallBanner({ status }) {
    const nav = useNavigate();
    // Freemium (v1.5.0): the old "trial ended → read-only" banner is gone —
    // free captains keep write access to their vessel. This banner now only
    // surfaces a payment problem on a PAID subscription (Stripe retrying the
    // card during the 3-day grace window).
    if (!status || status.subscription_status !== "payment_failed") return null;

    const showBilling = canShowPlans();
    const goPlans = () => (isNativeApp() ? openPaywall() : nav("/pricing"));

    return (
        <div
            data-testid="paywall-banner"
            role="alert"
            style={{
                background: "linear-gradient(90deg, rgba(248, 113, 113, 0.10), rgba(212, 181, 114, 0.12))",
                borderBottom: "1px solid var(--bm-gold-dim)",
                padding: "12px 24px",
                display: "flex",
                gap: 14,
                alignItems: "center",
                justifyContent: "center",
                flexWrap: "wrap",
            }}
        >
            <Lock size={16} color="var(--bm-gold)" />
            <div style={{ color: "var(--bm-text)", fontSize: 14, fontWeight: 500 }}>
                We couldn't process your last payment — please update your card to keep your <strong>Pro</strong> features.
            </div>
            {showBilling && (
                <button
                    data-testid="paywall-subscribe-btn"
                    onClick={goPlans}
                    className="bm-btn bm-btn-gold"
                    style={{ padding: "6px 14px", fontSize: 13 }}
                >
                    Fix payment
                </button>
            )}
        </div>
    );
}

export function useBillingStatus(refreshKey = 0) {
    const [status, setStatus] = useState(null);
    useEffect(() => {
        let cancelled = false;
        api.get("/billing/status")
            .then(({ data }) => { if (!cancelled) setStatus(data); })
            .catch(() => { if (!cancelled) setStatus(null); });
        return () => { cancelled = true; };
    }, [refreshKey]);
    return status;
}

export function ManageSubscriptionLink({ label = "Change plan" }) {
    const nav = useNavigate();
    if (!canShowPlans()) return null;
    const click = () => (isNativeApp() ? openPaywall() : nav("/pricing"));
    return (
        <button
            data-testid="manage-subscription-btn"
            onClick={click}
            className="bm-btn bm-btn-ghost"
            style={{ padding: "8px 12px" }}
        >
            <ExternalLink size={14} /> {label}
        </button>
    );
}

export function StripePortalLink({ label = "Cancel or update payment method" }) {
    if (!billingUIEnabled()) return null;
    const click = async () => {
        try {
            const { data } = await api.post("/billing/portal", {
                return_url: window.location.origin + "/pricing",
            });
            window.location.href = data.url;
        } catch (e) {
            alert(e.response?.data?.detail || "Could not open billing portal");
        }
    };
    return (
        <button
            data-testid="stripe-portal-btn"
            onClick={click}
            className="bm-link"
            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}
        >
            {label} →
        </button>
    );
}
